import { useCallback } from 'react';

import { OutletModel } from '@pukka/api';
import { dateUtils } from '@pukka/common/utils';
import { Alert, Badge, Switch, Table, Text, Tooltip } from '@pukka/ui-components';
import { CellContext } from '@tanstack/react-table';
import { useSnapshot } from 'valtio';

import { BillingPersistedState } from '../Billing.state';

import { ReactivateOutletModal } from './ReactivateOutletModal';
import { RecurringOutletState } from './RecurringOutlet.state';

function AddressCell({ row }: CellContext<OutletModel.Outlet, unknown>) {
  const { address, kecamatan, city } = row.original;

  return (
    <div>
      {address && <Text className="truncate">{address}</Text>}
      <Text variant="body-1" className="truncate text-neutral-400">
        {kecamatan ? `${kecamatan}, ` : ''}
        {city}
      </Text>
    </div>
  );
}

function StatusCell({ row }: CellContext<OutletModel.Outlet, unknown>) {
  const { isExpired } = row.original;

  return <Badge text={isExpired ? 'Inactive' : 'Active'} color={isExpired ? 'red' : 'green'} type="invert" size="md" />;
}

function RenewCell({ row }: CellContext<OutletModel.Outlet, unknown>) {
  const { selectedOutlet } = useSnapshot(RecurringOutletState);
  const { handleToggleOutlet } = RecurringOutletState;

  const { is_active_subscription: isActiveSubscription, disableToggle, id } = row.original;

  const handleToggle = useCallback(
    (checked: boolean) => {
      handleToggleOutlet(row.original, checked);
    },
    [row.original, handleToggleOutlet],
  );

  const loading = id === selectedOutlet?.id;
  const checked = loading ? !isActiveSubscription : isActiveSubscription;
  const className = 'align-middle';

  return disableToggle ? (
    <Tooltip
      trigger={
        <div className="w-fit">
          <Switch checked={checked} disabled loading={loading} className={className} />
        </div>
      }
    >
      <Text color="white" variant="caption-1">
        at least one outlet needs to be active
      </Text>
    </Tooltip>
  ) : (
    <Switch checked={checked} loading={loading} onCheckedChange={handleToggle} className={className} />
  );
}

const columns = [
  {
    header: 'Outlet name',
    accessorKey: 'name',
    minSize: 240,
  },
  {
    header: 'Address',
    cell: AddressCell,
    minSize: 300,
  },
  {
    header: 'Created in',
    accessorFn: (originalRow: OutletModel.Outlet) => dateUtils.formatDate(originalRow.created_at, 'd MMMM yyyy'),
  },
  {
    header: 'Renew/active until',
    accessorFn: (originalRow: OutletModel.Outlet) => dateUtils.formatDate(originalRow.end_date, 'd MMMM yyyy'),
  },
  {
    header: 'Status',
    cell: StatusCell,
    maxSize: 80,
  },
  {
    header: 'Renew',
    cell: RenewCell,
    maxSize: 80,
  },
];

function RecurringOutletTab() {
  const { recurring } = useSnapshot(BillingPersistedState);
  const { selectedOutlet, showPrompt, prorate, toggling } = useSnapshot(RecurringOutletState);
  const { closePrompt, handleOutletActivation, useOutletData } = RecurringOutletState;

  const { data: outlets = [] } = useOutletData();

  return (
    <div>
      <Text as="h4" color="black" weight="semibold" variant="subtitle-2" className="mb-4 p-4">
        Outlet List
      </Text>

      <div className="overflow-scroll">
        <Table rounded columns={columns} data={outlets} />
      </div>

      {selectedOutlet && showPrompt === 'deletion' ? (
        <Alert
          open
          loading={toggling === 'deletion'}
          type="danger"
          title="Deactivate Outlet?"
          okButtonName="Yes, deactivate"
          description={
            <Text>
              <strong>Are you sure you want to deactivate {selectedOutlet.name}?</strong>
              <br />
              You will not be charged for this outlet after its expiry date because it will not be renewed.
            </Text>
          }
          onOpenChange={closePrompt}
          onConfirm={() => handleOutletActivation(selectedOutlet.id, false)}
        />
      ) : null}
      {selectedOutlet && showPrompt === 'activation' ? (
        <ReactivateOutletModal
          open
          name={selectedOutlet.name}
          packages={recurring?.plan}
          period={prorate?.period}
          price={prorate?.price}
          suffix={prorate?.suffix}
          loading={!prorate || (toggling === 'activation' || toggling === 'immediate' ? toggling : undefined)}
          onToggleModal={closePrompt}
          onActivateNextPayment={() => handleOutletActivation(selectedOutlet.id, true, false)}
          onImmediateActivate={() => handleOutletActivation(selectedOutlet.id, true, true)}
        />
      ) : null}
    </div>
  );
}

export { RecurringOutletTab };
